import * as T from "three";
import { CITY, type CircleObstacle } from "./world-data.ts";
import { VEHICLE_STYLES, type VehicleStyle } from "./styles.ts";
import type { ModelAsset } from "./assets.ts";

// Moving traffic. Cars drive one-way loops round each block in the inner lane
// on the block's side of the street, keeping the block on their left (UK, drive
// on the left). Loops never cross each other, so cars only need to give way to
// the car in front and to the player.
const LANE = 1.5; // inner lane centre, metres from the street centre line
const CORNER = 3; // turning radius at crossroads

type Loop = { cx: number; cz: number };
const half = CITY.L / 2 - LANE;
const straight = 2 * (half - CORNER);
const arc = (Math.PI / 2) * CORNER;
export const perimeter = 4 * (straight + arc);

/** Point and heading at distance d along a block's rounded loop, with the block on the driver's left. */
export function along(loop: Loop, d: number) {
  d = ((d % perimeter) + perimeter) % perimeter;
  // Sides in order: south (+z, heading +x), east (+x, heading -z), north (-z, heading -x), west (-x, heading +z).
  const sides = [
    { sx: -half + CORNER, sz: half, hx: 1, hz: 0 },
    { sx: half, sz: half - CORNER, hx: 0, hz: -1 },
    { sx: half - CORNER, sz: -half, hx: -1, hz: 0 },
    { sx: -half, sz: -half + CORNER, hx: 0, hz: 1 },
  ];
  const seg = straight + arc;
  const i = Math.floor(d / seg), t = d - i * seg, s = sides[i], n = sides[(i + 1) % 4];
  if (t < straight) return { x: loop.cx + s.sx + s.hx * t, z: loop.cz + s.sz + s.hz * t, heading: Math.atan2(s.hx, s.hz) };
  // Quarter turn from this side's heading to the next side's heading.
  const f = (t - straight) / arc, a = f * (Math.PI / 2);
  const ex = s.sx + s.hx * straight, ez = s.sz + s.hz * straight; // turn start
  // Centre of the turn lies towards the inside (left of travel).
  const lx = s.hz, lz = -s.hx; // left of heading
  const ox = ex + lx * CORNER, oz = ez + lz * CORNER;
  const px = ox - lx * CORNER * Math.cos(a) + s.hx * CORNER * Math.sin(a);
  const pz = oz - lz * CORNER * Math.cos(a) + s.hz * CORNER * Math.sin(a);
  const hx = s.hx * Math.cos(a) + n.hx * Math.sin(a), hz = s.hz * Math.cos(a) + n.hz * Math.sin(a);
  return { x: loop.cx + px, z: loop.cz + pz, heading: Math.atan2(hx, hz) };
}

export function traffic(scene: T.Scene, count: number, seed = 11) {
  let s = seed;
  const rand = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const loops: Loop[] = [-CITY.L, 0, CITY.L].flatMap((cx) => [-CITY.L, 0, CITY.L].map((cz) => ({ cx, cz })));
  // Spread cars over loops; cars on the same loop start evenly spaced.
  const perLoop = new Map<Loop, number>();
  const cars = Array.from({ length: count }, (_, i) => {
    const loop = loops[i % loops.length];
    const k = perLoop.get(loop) ?? 0;
    perLoop.set(loop, k + 1);
    const share = Math.ceil(count / loops.length);
    return { loop, d: k * (perimeter / share) + rand() * 10, speed: 0, cruise: 7 + rand() * 3, model: Math.floor(rand() * 100) };
  });
  let meshes: T.InstancedMesh[] = [];
  let slots: { mesh: T.InstancedMesh; index: number }[] = [];
  let style: (typeof VEHICLE_STYLES)[VehicleStyle] | null = null;
  const obstacles: CircleObstacle[] = cars.flatMap(() => [{ x: 0, z: 1e6, r: 1.25 }, { x: 0, z: 1e6, r: 1.25 }]);
  const m = new T.Matrix4(), q = new T.Quaternion(), up = new T.Vector3(0, 1, 0), at = new T.Vector3(), sc = new T.Vector3();

  function setStyle(id: VehicleStyle, models: Map<string, ModelAsset>) {
    for (const mesh of meshes) scene.remove(mesh);
    style = VEHICLE_STYLES[id];
    const byModel = new Map<string, number[]>();
    cars.forEach((c, i) => {
      const name = style!.models[c.model % style!.models.length];
      if (!byModel.has(name)) byModel.set(name, []);
      byModel.get(name)!.push(i);
    });
    meshes = [];
    slots = new Array(cars.length);
    for (const [name, ids] of byModel) {
      const asset = models.get(name);
      if (!asset) continue;
      const mesh = new T.InstancedMesh(asset.geometry, asset.materials, ids.length);
      mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false; // instances move every frame
      ids.forEach((carIndex, i) => (slots[carIndex] = { mesh, index: i }));
      scene.add(mesh);
      meshes.push(mesh);
    }
  }

  function update(dt: number, player: T.Vector3) {
    if (!style) return;
    cars.forEach((c, i) => {
      const p = along(c.loop, c.d);
      // Give way: the player standing in the lane ahead, or the car in front.
      const fx = Math.sin(p.heading), fz = Math.cos(p.heading);
      const rx = player.x - p.x, rz = player.z - p.z;
      const ahead = rx * fx + rz * fz, side = Math.abs(rx * fz - rz * fx);
      let blocked = ahead > 0 && ahead < 9 && side < 2.2;
      for (const o of cars)
        if (o !== c && o.loop === c.loop) {
          const gap = (((o.d - c.d) % perimeter) + perimeter) % perimeter;
          if (gap > 0 && gap < 10) blocked = true;
        }
      const target = blocked ? 0 : c.cruise;
      c.speed += Math.sign(target - c.speed) * Math.min(Math.abs(target - c.speed), (blocked ? 14 : 4) * dt);
      c.d += c.speed * dt;
      const now = along(c.loop, c.d);
      obstacles[i * 2].x = now.x + Math.sin(now.heading) * 1.1;
      obstacles[i * 2].z = now.z + Math.cos(now.heading) * 1.1;
      obstacles[i * 2 + 1].x = now.x - Math.sin(now.heading) * 1.1;
      obstacles[i * 2 + 1].z = now.z - Math.cos(now.heading) * 1.1;
      const slot = slots[i];
      if (!slot) return;
      m.compose(at.set(now.x, -0.15, now.z), q.setFromAxisAngle(up, now.heading + style!.turn), sc.setScalar(style!.scale));
      slot.mesh.setMatrixAt(slot.index, m);
    });
    for (const mesh of meshes) mesh.instanceMatrix.needsUpdate = true;
  }
  return { setStyle, update, obstacles };
}
